import React, { Component, createRef } from 'react';
import { WidgetHelper } from '../WidgetHelper';
import { FaqThemeListWidget, FaqListWidget } from '.';
import { IFaq, ITheme, IFaqSearchParams } from '../../Models';
import { FaqService } from '../../Services';

interface IFaqSearchWidgetProps {
    configId: number;
    title: string;
    description: string;
    language: string;
    showSearchForm: boolean;
    showAllFaqIfNoThemeSelected: boolean;
    showNoResultIfNoFaq: string | undefined;
    currentFaqId: number | undefined;
    dictMessage: string | undefined;
    dictOpinionLabel: string;
    dictOpinionVotedLabel: string;
    dictSearchButtonLabel: string;
    dictClearButtonLabel: string;
    dictThemeAllLabel: string | undefined;
    dictNoResultTitle: string;
    dictNoResultDescription: string;
    dictCallUsIntroduction: string;
    dictYes: string;
    dictNo: string;
    dictCommentSuccess: string;
    dictCommentFail: string;
    dictSend: string;
    dictCommentInvite: string;
    dictYourComment: string;
    contactUrl: string;
    contactString: string;
    searchString: string;
}

interface IFaqSearchWidgetState {
    faqs: IFaq[];
    currentFaqs: IFaq[];
    themes: ITheme[];
    currentThemeId: number;
    searchText: string;
    currentSearchText: string;
    isLoading: boolean;
    allThemes: ITheme[];
}

export class FaqSearchWidget extends Component<
    IFaqSearchWidgetProps,
    IFaqSearchWidgetState
> {
    private readonly faqListRef = createRef<HTMLDivElement>();

    constructor(props: IFaqSearchWidgetProps) {
        super(props);
        window.history.pushState({}, '', window.location.pathname);
        this.state = {
            faqs: [],
            currentFaqs: [],
            themes: [],
            currentThemeId: 0,
            searchText: this.props.searchString,
            currentSearchText: this.props.searchString,
            isLoading: true,
            allThemes: [],
        };

        void this.getAllFaq(this.state.currentSearchText, null);
    }

    async getAllFaq(searchWords: string | null, themeId: number | null) {
        try {
            const faqSearchParams: IFaqSearchParams = {
                configId: this.props.configId,
                themeId,
                searchWords,
            };
            const faqs = await FaqService.getAllFaq(faqSearchParams);
            const allThemes = await FaqService.getAllThemes(
                this.props.configId
            );
            if (themeId && themeId != -1) {
                this.setState({
                    faqs,
                    currentFaqs: faqs,
                    isLoading: false,
                    allThemes,
                });
            } else {
                const themes = this.getFaqThemes(faqs);
                this.setState({
                    faqs,
                    currentFaqs: [],
                    themes: themes,
                    isLoading: false,
                    currentThemeId:
                        themes[0] && themes[0].id
                            ? themes[0].id
                            : allThemes[0].id,
                    allThemes,
                });
                this.handleTheme(
                    themes[0] && themes[0].id ? themes[0].id : allThemes[0].id
                );
            }
        } catch (e) {
            if (!(e instanceof DOMException) || e.name !== 'AbortError') {
                throw e;
            }
        }
    }

    getFaqThemes(faqs: IFaq[]): ITheme[] {
        const themes: ITheme[] = [];

        // collect & dedupe
        faqs.forEach((faq) => {
            (faq.themes ?? []).forEach((theme) => {
                if (!themes.some((t) => t.id === theme.id)) {
                    themes.push(theme);
                }
            });
        });

        // sort by sortOrder ASC
        return themes.sort((a, b) => {
            const aOrder = a.sortOrder ?? 9999;
            const bOrder = b.sortOrder ?? 9999;
            return aOrder - bOrder;
        });
    }

    handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ searchText: event.target.value });
    };

    handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        this.setState(
            {
                currentThemeId: -1,
                currentSearchText: this.state.searchText,
            },
            () =>
                void this.searchFaq(
                    this.state.currentThemeId,
                    this.state.currentSearchText
                )
        );
    };

    handleTheme = (themeId: number) => {
        void this.searchFaq(themeId, this.state.currentSearchText);

        // mobile functionality
        if (window.innerWidth < 992 && this.faqListRef.current) {
            this.faqListRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }
    };

    searchFaq = async (themeId: number, keyword: string) => {
        await this.getAllFaq(keyword, themeId);

        const themes = this.getFaqThemes(this.state.faqs);

        let currentThemeId = -1;
        if (themes.length > 0) {
            currentThemeId = themes.length >= 1 ? themeId : themes[0].id;
        }

        this.setState({
            currentThemeId,
            isLoading: false,
        });
    };

    clearSearch = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        this.setState(
            {
                searchText: '',
                currentSearchText: '',
                currentThemeId: -1,
            },
            () => {
                void this.searchFaq(
                    this.state.currentThemeId,
                    this.state.currentSearchText
                );
            }
        );
    };

    render() {
        let searchForm: JSX.Element;

        if (this.props.showSearchForm) {
            // show complete form with search field and themes
            searchForm = (
                <div className="banner banner--colored banner--colored__faq">
                    <div className="container">
                        <div className="row">
                            <div className="col-12 col-lg-8 offset-lg-2">
                                <h1>{this.props.title}</h1>
                                <form
                                    className="faq-search form-search"
                                    onSubmit={this.handleSubmit}
                                    data-skip-captcha="true"
                                >
                                    <div className="form-group">
                                        <label className="sr-only sr-only-focusable">
                                            {this.props.description}
                                        </label>
                                        <input
                                            title={this.props.description}
                                            type="text"
                                            name="search"
                                            placeholder={this.props.description}
                                            value={this.state.searchText}
                                            onChange={this.handleChange}
                                        />
                                    </div>
                                    {!!this.state.searchText && (
                                        <div className="form-group clear">
                                            <button
                                                type="button"
                                                className="btn--icon"
                                                onClick={this.clearSearch}
                                            >
                                                <i className="icon-cross"></i>
                                                {
                                                    this.props
                                                        .dictClearButtonLabel
                                                }
                                            </button>
                                        </div>
                                    )}
                                    <div className="form-group submit">
                                        <button type="submit">
                                            <i className="icon-search"></i>
                                            {this.props.dictSearchButtonLabel}
                                        </button>
                                    </div>
                                </form>
                                {this.state.themes &&
                                    this.state.themes.length > 0 && (
                                        <FaqThemeListWidget
                                            themes={this.state.themes}
                                            currentThemeId={
                                                this.state.currentThemeId
                                            }
                                            onClick={this.handleTheme}
                                            coloredFaq={true}
                                        />
                                    )}
                            </div>
                        </div>
                    </div>
                </div>
            );
        } else {
            // show only themes
            searchForm = (
                <>
                    {this.state.themes.length > 0 && (
                        <div className="before-wrapper">
                            <div className="contact-theme-container">
                                <h3>{this.props.dictCallUsIntroduction}</h3>
                                <div className="container">
                                    <div className="row">
                                        <div className="col-12 col-lg-8 offset-lg-2">
                                            {this.state.themes &&
                                                this.state.themes.length >
                                                    0 && (
                                                    <FaqThemeListWidget
                                                        themes={
                                                            this.state.themes
                                                        }
                                                        currentThemeId={
                                                            this.state
                                                                .currentThemeId
                                                        }
                                                        onClick={
                                                            this.handleTheme
                                                        }
                                                        coloredFaq={false}
                                                    />
                                                )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            );
        }

        const listFaq = (
            <div className="container">
                <div className="row">
                    <div
                        className="col-12 col-lg-8 offset-lg-2"
                        ref={this.faqListRef}
                    >
                        <FaqListWidget
                            currentFaqId={this.props.currentFaqId}
                            faqs={this.state.currentFaqs}
                            opinionLabel={this.props.dictOpinionLabel}
                            opinionVotedLabel={this.props.dictOpinionVotedLabel}
                            dictNoResultTitle={this.props.dictNoResultTitle}
                            dictNoResultDescription={
                                this.props.dictNoResultDescription
                            }
                            isLoading={this.state.isLoading}
                            showNoResult={
                                this.props.showNoResultIfNoFaq != undefined &&
                                this.props.showNoResultIfNoFaq.toUpperCase() ==
                                    'TRUE'
                            }
                            dictYes={this.props.dictYes}
                            dictNo={this.props.dictNo}
                            dictSend={this.props.dictSend}
                            dictCommentSuccess={this.props.dictCommentSuccess}
                            dictCommentFail={this.props.dictCommentFail}
                            dictCommentInvite={this.props.dictCommentInvite}
                            dictYourComment={this.props.dictYourComment}
                            contactString={this.props.contactString}
                            contactUrl={this.props.contactUrl}
                        />
                    </div>
                </div>
            </div>
        );

        return (
            <>
                {searchForm}
                {this.props.dictMessage && this.props.dictMessage.length > 0 ? (
                    <div className="contact-list-container">
                        <h3>{this.props.dictMessage}</h3>
                        {listFaq}
                    </div>
                ) : (
                    <>{listFaq}</>
                )}
            </>
        );
    }
}

WidgetHelper.register(FaqSearchWidget, 'faq-search-widget');
